import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import LoadingOverlay from "../components/LoadingOverlay";

const DashboardCard = ({ title, value, icon, gradient }) => {
  return (
    <div className="col-xl-3 col-md-6 mb-4">
      <div className="dashboard-card" style={{ background: gradient }}>
        <div className="dashboard-card-body">
          <div className="dashboard-card-content">
            <div className="dashboard-card-text">
              <span className="dashboard-card-title">{title}</span>
              <span className="dashboard-card-value">{value}</span>
            </div>

            <div className="dashboard-card-icon">
              <i className={`fas ${icon}`}></i>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const normalizeRole = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "");

const DASHBOARD_ACTIONS = {
  SUPERADMIN: "superadmin.getDashboard",
  ADMIN: "admin.getDashboard",
  OWNER: "owner.getDashboard",
  TENANT: "tenant.getDashboard",
};

const emptyCounts = { superAdmin: 0, admin: 0, owner: 0, tenant: 0 };

const Dashboard = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  const userId = localStorage.getItem("userid");
  const role = normalizeRole(localStorage.getItem("role"));

  const [isLoading, setIsLoading] = useState(false);
  const [counts, setCounts] = useState(emptyCounts);

  const fetchDashboardCounts = useCallback(async () => {
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL");
      return;
    }

    if (!userId) {
      toast.error("Missing userId. Please login again.");
      return;
    }

    const action = DASHBOARD_ACTIONS[role];
    if (!action) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(API_BASE_URL, { action, userId });
      const data = response?.data;

      if (data?.status === "success") {
        const d = data?.data || {};
        setCounts({
          superAdmin: d.superAdmins || 0,
          admin: d.admins || 0,
          owner: d.owners || 0,
          tenant: d.tenants || 0,
        });
      } else {
        toast.error(data?.message || "Failed to load dashboard");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong"
      );
    } finally {
      setIsLoading(false);
    }
  }, [API_BASE_URL, userId, role]);

  useEffect(() => {
    fetchDashboardCounts();
  }, [fetchDashboardCounts]);

  return (
    <div className="employee-form p-4">
      <div className="content-header">
        <h1>Dashboard</h1>
        <p>Overview of platform statistics</p>
      </div>

      {isLoading ? (
        <div style={{ position: "relative", minHeight: 280 }}>
          <LoadingOverlay open variant="content" size={160} />
        </div>
      ) : (
        <div className="row">
          {role === "SUPERADMIN" && (
            <DashboardCard
              title="Super Admin"
              value={counts.superAdmin}
              icon="fa-user-shield"
              gradient="linear-gradient(135deg, #42e695, #3bb2b8)"
            />
          )}

          {role === "SUPERADMIN" && (
            <DashboardCard
              title="Admin"
              value={counts.admin}
              icon="fa-user-tie"
              gradient="linear-gradient(135deg, #ffb347, #ff7043)"
            />
          )}

          {(role === "SUPERADMIN" || role === "ADMIN") && (
            <DashboardCard
              title="Owner"
              value={counts.owner}
              icon="fa-user"
              gradient="linear-gradient(135deg, #a18cd1, #fbc2eb)"
            />
          )}

          <DashboardCard
            title="Tenant"
            value={counts.tenant}
            icon="fa-users"
            gradient="linear-gradient(135deg,#00c6ff,#0072ff)"
          />
        </div>
      )}

      <style jsx>{`
        .dashboard-card {
          border-radius: 12px;
          color: white;
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
        }

        .dashboard-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 10px 22px rgba(0, 0, 0, 0.15);
        }

        .dashboard-card-body {
          padding: 24px;
        }

        .dashboard-card-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .dashboard-card-text {
          display: flex;
          flex-direction: column;
        }

        .dashboard-card-title {
          font-size: 16px;
          font-weight: 500;
          margin-bottom: 6px;
        }

        .dashboard-card-value {
          font-size: 38px;
          font-weight: 700;
          line-height: 1;
        }

        .dashboard-card-icon {
          font-size: 40px;
          opacity: 0.4;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;